import { Suspense, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';

import { UserRole } from '@repo/types';
// Route constants
import {
  ROUTES,
  AUTH_ROUTES,
  REPORTS_ROUTES,
  SETTINGS_ROUTES,
  JUNIPER_ROUTES,
  SUPPORT_ROUTES,
  PUBLIC_ROUTES,
  AR_COLLECTIONS_ROUTES,
} from '../shared/constants';

// Feature imports
import {
  LoginPage,
  AuthCallbackPage,
  ClioCallbackPage,
  QboCallbackPage,
} from '../features/auth/pages';
import { ReportsPage } from '../features/reports/pages';
import { ARDashboardPage, ARHistoryPage } from '../features/reports/pages/arReportPage';
import {
  TrustDashboardPage,
  TrustHistoryPage,
} from '../features/reports/pages/trustReconciliationPage';
import {
  MatterLedgerDashboardPage,
  MatterLedgerHistoryPage,
} from '../features/reports/pages/matterLedgerReportPage';
import {
  MatterStatementDashboardPage,
  MatterStatementHistoryPage,
} from '../features/reports/pages/matterStatementReportPage';
import { SettingsPage, ReportingConfigPage } from '../features/settings/pages';
import {
  ClioWebhookLifecycleManagerPage,
  WebhookActivityPage,
  QboWebhookSettingsPage,
  QboWebhookActivityPage,
} from '../features/webhooks/pages';
import {
  JuniperConfigPage,
  JuniperTransactionsPage,
  PdfViewerPage,
  DocumentViewerPage,
} from '../features/juniper/pages';
import { ExternalArchivalPage } from '../features/archival/pages';
import { TemplateManagerPage } from '../features/templates/pages';
import { FeedbackPage } from '../features/support/pages/feedbackPage/FeedbackPage';
import { TrustRequestCreationPage } from '../features/trust-requests/pages';
import {
  ArCollectionsPage,
  ArSettingsPage,
  EmailQueuePage,
  AppealsPage,
  WriteOffsPage,
  PerformancePage,
  LedgerReportsPage,
  EscalationReportPage,
} from '../features/ar-collections/pages';
// Shared pages
import {
  NotFoundPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  ForbiddenPage,
} from '../shared/pages';
// Permission
import { AbilityProvider } from '../providers/AbilityProvider';
import { Action, Platform, useGetRoleCurrentUser } from '../shared';
import { useDynamicTitle } from '../shared/hooks/useDynamicTitle';
import {
  getDefaultRouterLoggedInCallback,
  PERMISSION_SUBJECT_ROUTES,
  PermissionProtectedRoute,
} from '../features/permissions';
import { Layout, ProtectedRoute } from '../shared/layout';
import { ArAttorneyResponsePage } from '../features/ar-collections/pages/ArAttorneyResponsePage';

const currentPlatform = (import.meta.env.VITE_PLATFORM as Platform) || Platform.LYDECKER;

function Root() {
  useDynamicTitle();
  return <Outlet />;
}

export function AppRouter() {
  const role = useGetRoleCurrentUser();
  const defaultRedirectRoute = useMemo(() => {
    return getDefaultRouterLoggedInCallback(currentPlatform as Platform, role as UserRole);
  }, [role]);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: <Root />,
          children: [
            // ============================================
            // Protected Routes (Main App)
            // ============================================
            {
              path: ROUTES.HOME,
              element: (
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              ),
              children: [
                // Default redirect
                { index: true, element: <Navigate to={defaultRedirectRoute} replace /> },
                // ============================================
                // Ar Collections
                // ============================================
                {
                  path: AR_COLLECTIONS_ROUTES.BASE.replace('/', ''),
                  element: <Navigate to={AR_COLLECTIONS_ROUTES.AR.COLLECTIONS} replace />,
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.COLLECTIONS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.QUEUE}
                      action={Action.MANAGE}
                    >
                      <ArCollectionsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.SETTINGS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <ArSettingsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.EMAIL_QUEUE.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING}
                      action={Action.MANAGE}
                    >
                      <EmailQueuePage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.APPEALS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING}
                      action={Action.MANAGE}
                    >
                      <AppealsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.WRITE_OFFS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING}
                      action={Action.MANAGE}
                    >
                      <WriteOffsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.PERFORMANCE.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING}
                      action={Action.MANAGE}
                    >
                      <PerformancePage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.LEDGER_REPORTS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING}
                      action={Action.MANAGE}
                    >
                      <LedgerReportsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.AR.ESCALATION_REPORT.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING}
                      action={Action.MANAGE}
                    >
                      <EscalationReportPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: AR_COLLECTIONS_ROUTES.PUBLIC.APPEAL_DETAIL_ATTORNEY_RESPONSE.replace(
                    '/',
                    ''
                  ),
                  element: <ArAttorneyResponsePage />,
                },
                // ============================================
                // Reports Routes
                // ============================================
                {
                  path: REPORTS_ROUTES.BASE.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <ReportsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.AR.BASE.replace('/', ''),
                  element: <Navigate to={REPORTS_ROUTES.AR.DASHBOARD} replace />,
                },
                {
                  path: REPORTS_ROUTES.AR.DASHBOARD.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <ARDashboardPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.AR.HISTORY.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <ARHistoryPage />{' '}
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.TRUST.BASE.replace('/', ''),
                  element: <Navigate to={REPORTS_ROUTES.TRUST.DASHBOARD} replace />,
                },
                {
                  path: REPORTS_ROUTES.TRUST.DASHBOARD.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <TrustDashboardPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.TRUST.HISTORY.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <TrustHistoryPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.MATTER_LEDGER.BASE.replace('/', ''),
                  element: <Navigate to={REPORTS_ROUTES.MATTER_LEDGER.DASHBOARD} replace />,
                },
                {
                  path: REPORTS_ROUTES.MATTER_LEDGER.DASHBOARD.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <MatterLedgerDashboardPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.MATTER_LEDGER.HISTORY.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <MatterLedgerHistoryPage />{' '}
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.MATTER_STATEMENT.BASE.replace('/', ''),
                  element: <Navigate to={REPORTS_ROUTES.MATTER_STATEMENT.DASHBOARD} replace />,
                },
                {
                  path: REPORTS_ROUTES.MATTER_STATEMENT.DASHBOARD.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <MatterStatementDashboardPage />{' '}
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: REPORTS_ROUTES.MATTER_STATEMENT.HISTORY.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.REPORTS}
                      action={Action.MANAGE}
                    >
                      <MatterStatementHistoryPage />
                    </PermissionProtectedRoute>
                  ),
                },

                // ============================================
                // Settings Routes
                // ============================================
                {
                  path: SETTINGS_ROUTES.BASE.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <SettingsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.REPORTING_CONFIG.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <ReportingConfigPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.ARCHIVAL.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <ExternalArchivalPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.JUNIPER_CONFIG.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <JuniperConfigPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.TEMPLATE_MANAGER.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <TemplateManagerPage />{' '}
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.WEBHOOKS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <ClioWebhookLifecycleManagerPage />{' '}
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.WEBHOOK_ACTIVITY.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <WebhookActivityPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.QBO_WEBHOOKS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <QboWebhookSettingsPage />
                    </PermissionProtectedRoute>
                  ),
                },
                {
                  path: SETTINGS_ROUTES.QBO_WEBHOOK_ACTIVITY.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SETTINGS}
                      action={Action.MANAGE}
                    >
                      <QboWebhookActivityPage />
                    </PermissionProtectedRoute>
                  ),
                },

                // ============================================
                // Juniper Routes
                // ============================================
                {
                  path: JUNIPER_ROUTES.TRANSACTIONS.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.JUNIPER_TRANSACTIONS}
                      action={Action.MANAGE}
                    >
                      <JuniperTransactionsPage />
                    </PermissionProtectedRoute>
                  ),
                },

                // ============================================
                // Trust Request Routes
                // ============================================
                {
                  path: 'trust-requests/create',
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.TRUST_REQUESTS}
                      action={Action.MANAGE}
                    >
                      <TrustRequestCreationPage />{' '}
                    </PermissionProtectedRoute>
                  ),
                },

                // ============================================
                // Support Routes
                // ============================================
                {
                  path: SUPPORT_ROUTES.FEEDBACK.replace('/', ''),
                  element: (
                    <PermissionProtectedRoute
                      subject={PERMISSION_SUBJECT_ROUTES.SUPPORT}
                      action={Action.MANAGE}
                    >
                      <FeedbackPage />
                    </PermissionProtectedRoute>
                  ),
                },
              ],
            },

            // ============================================
            // Special Protected Routes (Viewers)
            // ============================================
            {
              path: JUNIPER_ROUTES.PDF_VIEWER,
              element: (
                <ProtectedRoute>
                  <PermissionProtectedRoute
                    subject={PERMISSION_SUBJECT_ROUTES.JUNIPER_PDF_VIEWER}
                    action={Action.MANAGE}
                  >
                    <PdfViewerPage />
                  </PermissionProtectedRoute>
                </ProtectedRoute>
              ),
            },
            {
              path: JUNIPER_ROUTES.DOCUMENT_VIEWER,
              element: (
                <ProtectedRoute>
                  <PermissionProtectedRoute
                    subject={PERMISSION_SUBJECT_ROUTES.JUNIPER_DOCUMENT_VIEWER}
                    action={Action.MANAGE}
                  >
                    <DocumentViewerPage />
                  </PermissionProtectedRoute>
                </ProtectedRoute>
              ),
            },

            // ============================================
            // Public Routes (Auth)
            // ============================================
            {
              path: ROUTES.LOGIN,
              element: <LoginPage />,
            },
            {
              path: AUTH_ROUTES.CALLBACK,
              element: <AuthCallbackPage />,
            },
            {
              path: AUTH_ROUTES.ERROR,
              element: <AuthCallbackPage />,
            },
            {
              path: AUTH_ROUTES.CLIO_REDIRECT,
              element: <ClioCallbackPage />,
            },
            {
              path: AUTH_ROUTES.QBO_REDIRECT,
              element: <QboCallbackPage />,
            },

            // ============================================
            // Public Routes (Legal)
            // ============================================
            {
              path: PUBLIC_ROUTES.PRIVACY_POLICY,
              element: <PrivacyPolicyPage />,
            },
            {
              path: PUBLIC_ROUTES.TERMS_OF_SERVICE,
              element: <TermsOfServicePage />,
            },
            {
              path: ROUTES.FORBIDDEN,
              element: <ForbiddenPage />,
            },

            // ============================================
            // Fallback Route
            // ============================================
            {
              path: ROUTES.NOT_FOUND,
              element: <NotFoundPage />,
            },
          ],
        },
      ]),
    [defaultRedirectRoute]
  );

  return (
    <Suspense fallback>
      <AbilityProvider permission={{ platform: currentPlatform, role: role as UserRole }}>
        <RouterProvider router={router} />
      </AbilityProvider>
    </Suspense>
  );
}
